<template>
    <card-grid-item
        :tab-widget="tabWidget"
        :card-class="['card-chart']"
        :min-w="3"
        :min-h="minHeight"
        @tab-widget-settings-click="handleTabWidgetSettingsClick"
    >
        <template v-slot:card-title>
            {{ title }}
        </template>
        <template v-slot:card-body>
            <busy-modal :show="busy" class="chart-widget-spinner" />
            <vue-apex-charts v-if="ready" height="400" :options="options" :series="series" />
        </template>
        <template v-slot:tab-widget-settings-modal>
            <tab-widget-settings-modal-form
                :modal-id="tabWidgetSettingsModalID"
                :update="updateTabWidgetSettings"
                :initial-values="tabWidgetSettingsInitialValues"
                :with-chart-period="true"
                :with-chart-interval="true"
                @success-update="handleSuccessTabWidgetSettingsUpdate"
            ></tab-widget-settings-modal-form>
        </template>
    </card-grid-item>
</template>

<script>
import API from '../../../../api';
import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
import CardGridItem from '../common/card-grid-item.vue';
import {
    DEFAULT_DASHBOARD_CHART_INTERVAL,
    getApexChartsGradientFill,
    getApexChartsDateFormatBasedOnInterval,
    getDefaultChartPeriodBasedOnInterval,
} from '../helpers';
import { DASHBOARD_PAID_SUBSCRIBERS_BY_DATE_LIST_ENDPOINT } from '../../../../endpoints';
import max from 'lodash/max';
import min from 'lodash/min';
import TabWidgetSettingsModalForm from '../common/tab-widget-settings-modal-form.vue';
import VueApexCharts from 'vue3-apexcharts';
import cardGridItemBaseMixin from '../common/card-grid-item-base-mixin';

export default {
    name: 'PaidSubscribersChartCardGridItem',
    mixins: [cardGridItemBaseMixin],
    components: {
        BusyModal,
        CardGridItem,
        VueApexCharts,
        TabWidgetSettingsModalForm,
    },
    computed: {
        tabWidgetSettingsInitialValues() {
            return {
                settings: {
                    name: this.tabWidget.settings.name || this.defaultName,
                    chart_interval: this.tabWidget.settings.chart_interval,
                    chart_period: this.tabWidget.settings.chart_period,
                },
            };
        },
        series() {
            return [
                {
                    name: 'Active',
                    data: this.paidSubscriberCountList.map((item) => item.active),
                },
                {
                    name: 'Average',
                    data: this.paidSubscriberCountList.map((item) => item.active_average),
                },
            ];
        },
        options() {
            return {
                chart: {
                    type: 'area',
                    toolbar: {
                        show: false,
                    },
                    zoom: {
                        enabled: false,
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    curve: 'straight',
                },
                fill: getApexChartsGradientFill(),
                grid: {
                    xaxis: {
                        lines: {
                            show: true,
                        },
                    },
                    padding: {
                        right: 30,
                        bottom: 10,
                    },
                },
                xaxis: {
                    type: 'datetime',
                    categories: this.paidSubscriberCountList.map((item) => item.date),
                    labels: {
                        datetimeFormatter: {
                            month: 'MMM',
                            day: 'MMM dd',
                        },
                    },
                },
                yaxis: [
                    {
                        tickAmount: 1,
                        min: this.minActiveY,
                        title: {
                            text: 'Active',
                        },
                        labels: {
                            formatter: (val) => `${val.toFixed()}`,
                        },
                    },
                    {
                        opposite: true,
                        min: this.minActiveAverageY,
                        title: {
                            text: 'Average',
                        },
                        labels: {
                            formatter: (val) => val && `$${val.toFixed(2)}`,
                        },
                    },
                ],
                tooltip: {
                    x: {
                        format: getApexChartsDateFormatBasedOnInterval(this.tabWidget.settings.chart_interval),
                    },
                },
            };
        },
    },
    mounted() {
        this.fetchPaidSubscribersCount();
    },
    data() {
        return {
            busy: null,
            paidSubscriberCountList: [],
            minActiveY: 0,
            minActiveAverageY: 0,
        };
    },
    methods: {
        async fetchPaidSubscribersCount() {
            try {
                this.busy = true;
                const interval = this.tabWidget.settings.chart_interval || DEFAULT_DASHBOARD_CHART_INTERVAL;
                const response = await API.get(DASHBOARD_PAID_SUBSCRIBERS_BY_DATE_LIST_ENDPOINT(), {
                    params: {
                        period: this.tabWidget.settings.chart_period || getDefaultChartPeriodBasedOnInterval(interval),
                        interval,
                    },
                });
                this.paidSubscriberCountList = response.data;

                const minActiveY = min(this.paidSubscriberCountList.map((item) => item.active));
                this.minActiveY = minActiveY ? max([minActiveY, 50]) - 50 : 0;

                const minActiveAverageY = min(
                    this.paidSubscriberCountList.map((item) => parseFloat(item.active_average))
                );
                this.minActiveAverageY = minActiveAverageY ? max([minActiveAverageY, 25]) - 25 : 0;
            } catch {
                this.errorToast({ text: 'Could not load paid subscribers.' });
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>
